import { useState, useCallback } from 'react'
import { useApiAccess, useUserSession } from 'features/session/context'
import { useForm, Controller } from 'react-hook-form'

import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Popover,
  Stack,
  TextField,
  Typography,
  Tooltip,
  IconButton
} from '@mui/material'

import { CloseIcon } from 'shared/icons'
import {
  Save as OverwriteIcon,
  LibraryAdd as SaveAsIcon
} from '@mui/icons-material';

import { DashBoardValue } from 'features/dashBoard/hooks'
import { ConfirmDialog } from 'components/dialog/confirm'

import { DashBoardProfileResult, PostDashBoardProfile, EditDashBoardProfile } from 'interface/dashBoardProfile'
import { SearchDetailResult } from 'interface/search'

type SaveProfileFormData = {
  title: string
  isSearchPeculiar: boolean
  isShared: boolean
}
const profileToFormData = (
  profileData: DashBoardProfileResult | null
): SaveProfileFormData => profileData ? {
  title: profileData.profileName,
  isShared: profileData.isShared,
  isSearchPeculiar: profileData.searchId ? true : false
} : {
  title: '',
  isSearchPeculiar: false,
  isShared: false,
}

type SaveDashBoardProfilePopoverProps<T> = {
  anchorEl: HTMLButtonElement | null
  profileKey: string
  search: SearchDetailResult | null
  dashBoard: DashBoardValue<T>
  onClose: () => void
}

export const SaveDashBoardProfilePopover = <T,>({
  anchorEl,
  profileKey,
  search,
  dashBoard: {
    selectedProfile: profileData,
    dashBoardItems,
    globalAxisKey,
    setCurrentProfile
  },
  onClose
}: SaveDashBoardProfilePopoverProps<T>) => {
  const { userId } = useUserSession()
  const apiAccess = useApiAccess()

  const [isErrorDialog, setIsErrorDialog] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const {
    control,
    handleSubmit,
    //setValue,
    //watch,
    formState: { isValid },
  } = useForm<SaveProfileFormData>({
    defaultValues: profileToFormData(profileData),
    mode: 'onChange',
  })

  const createDashboardProfile = useCallback(
    async (data: {
      title: string
      isSearchPeculiar: boolean
      isShared: boolean
    }) => {
      try {
        const response = await PostDashBoardProfile(
          apiAccess,
          profileKey,
          {
            searchId: data.isSearchPeculiar ? search?.searchId : null,
            profileName: data.title,
            description: '',
            profileData: {
              items: dashBoardItems,
              globalAxisKey
            },
            isShared: data.isShared,
          }
        )
        setCurrentProfile(response)
        onClose()
      } catch (e) {
        console.log(e)
        setIsErrorDialog(true)
        setErrorMessage('保存に失敗しました。')
      }
    },
    [apiAccess, dashBoardItems, globalAxisKey, search, setCurrentProfile, onClose, profileKey]
  )

  const patchDashboardProfile = useCallback(
    async (data: {
      title: string
      isSearchPeculiar: boolean
      isShared: boolean
    }) => {
      try {
        if (!profileData || !profileData.profileId) return

        const response = await EditDashBoardProfile(
          apiAccess,
          profileKey,
          profileData.profileId,
          {
            searchId: data.isSearchPeculiar ? search?.searchId : null,
            profileName: data.title,
            description: '',
            profileData: {
              items: dashBoardItems,
              globalAxisKey
            },
            isShared: data.isShared,
            updateTime: profileData.updateTime,
          }
        )
        setCurrentProfile(response)
        onClose()
      } catch (e) {
        console.log(e)
        setIsErrorDialog(true)
        setErrorMessage('保存に失敗しました。')
      }
    },
    [apiAccess, profileData, search, dashBoardItems, globalAxisKey, setCurrentProfile, onClose, profileKey]
  )

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      id="new-template-dialog"
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      sx={{
        maxWidth: '100%',
      }}
    >
      <Stack
        padding={2}
        sx={{
          minWidth: '30em'
        }}
      >
        <Stack direction='row' spacing={1} alignItems='center' marginBottom={2}>
          <Typography variant="h6" flexGrow={1} whiteSpace='nowrap' overflow='hidden' textOverflow='ellipsis'>
            レイアウトの保存
          </Typography>
          <Tooltip title="閉じる">
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack direction='column' spacing={1}>
          <FormGroup>
            <Controller
              control={control}
              name="title"
              rules={{
                required: '必須項目です。',
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="タイトル"
                  variant="outlined"
                  fullWidth
                  autoFocus
                />
              )}
            />
          </FormGroup>

          <FormGroup>
            <Controller
              control={control}
              name="isSearchPeculiar"
              render={({ field }) => (
                <FormControlLabel
                  label={`${search?.title} 専用`}
                  control={<Checkbox {...field} checked={field.value} />}
                />
              )}
            />
            <Controller
              control={control}
              name="isShared"
              render={({ field }) => (
                <FormControlLabel
                  label="他のユーザーに公開"
                  {...field}
                  control={<Checkbox {...field} checked={field.value} />}
                />
              )}
            />
          </FormGroup>
        </Stack>

        <Stack direction="row" justifyContent="flex-end" spacing={2} marginTop={2}>
          { profileData && profileData.userId === userId && (
            <Button
              variant="contained"
              disabled={!isValid}
              onClick={handleSubmit(patchDashboardProfile)}
              startIcon={<OverwriteIcon/>}
            >
              上書き
            </Button>
          ) }
          
          <Button
            variant="contained"
            disabled={!isValid}
            onClick={handleSubmit(createDashboardProfile)}
            startIcon={<SaveAsIcon/>}
          >
            新規保存
          </Button>

          <Button
            variant="text"
            onClick={onClose}
          >
            キャンセル
          </Button>
        </Stack>
      </Stack>

      { isErrorDialog && (
        <ConfirmDialog
          message={errorMessage}
          cancel={{
            label: '閉じる',
            onClick: () => setIsErrorDialog(false)
          }}    
        />
      )}      
    </Popover>
  )
}
